import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { acceptOwnerSuggestion, dismissOwnerSuggestion, quickAddOwner } from '../api/devices'
import { suggestionAction } from './ownerMarks'
import {
  massAssignDevices,
  removeUnassignedDevice,
  unassignedFiltersQuery,
  unassignedTableQuery,
  type UnassignedFilters,
} from '../api/inventoree'
import type { Named } from '../api/shell'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { UnassignedCard } from './UnassignedCard'
import { toast } from '../ui/notify'
import { unassignedColumns } from './columns'

/**
 * Inventoree New devices - overview/unassigned/index.php + OverviewUnassignedModel.
 *
 * Atribuirea in masa si Remove trimiteau mai departe la `/overview/massAssign`
 * si `/overview/removeDevice`. Nu mai raspunde nimeni acolo: Overview.php a
 * ramas numai cu `_remap`. Si nici mai devreme n-ar fi mers - un `fetch` fara
 * antetul `X-Requested-With` cadea pe `is_ajax_request()` din CI3, iar
 * formularul pleca si fara jetonul CSRF. Cum raspunsul se arunca
 * (`void fetch`), omul apasa "Assign selected", nu vedea nici o eroare, si
 * masinile ramaneau neatribuite. Amandoua merg acum la
 * /api/v2/inventoree/unassigned/*, iar raspunsul se si arata.
 *
 * Adaugarea rapida a unui om NU mai trimite acolo. `Overview::quickAddOwner` a
 * fost sters pe 6 septembrie 2026 ca „leftover AJAX nefolosit", fiindca s-a
 * cautat cine il cheama numai in CodeIgniter - ecranul asta, scris in React,
 * n-a fost vazut. Butonul „Add" trimitea de atunci intr-un 404 pe care
 * `void fetch(...)` il inghitea fara sa spuna nimic. Acum cere de la
 * `POST /api/v2/devices/{id}/owner/quick-add`, care face acelasi lucru prin
 * `Contact::create` si are aceeasi poarta.
 */

const MASS_CSS = `
    .ma-panel {
        background: #fff;
        border: 1px solid #e3e7ec;
        border-radius: 4px;
        margin-bottom: 14px;
        overflow: hidden;
        box-shadow: 0 1px 6px rgba(0, 0, 0, .05);
    }
    .ma-panel.ma-opening { animation: ma-open .18s ease; }
    @keyframes ma-open {
        from { opacity: 0; transform: translateY(-6px); }
        to   { opacity: 1; transform: none; }
    }
    .ma-head {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 14px;
        background: #f7f6f1;
        border-bottom: 1px solid #eef1f4;
    }
    .ma-count { font-size: 13px; color: #393F4C; }
    .ma-count strong { font-size: 18px; }
    .ma-hint { flex: 1 1 auto; font-size: 12px; color: #999; }
    .ma-fields {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 12px;
        padding: 14px;
    }
    .ma-field { flex: 1 1 190px; min-width: 170px; }
    .ma-field-action { flex: 0 0 auto; }
    .ma-field label {
        display: block;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: .04em;
        color: #999;
        margin-bottom: 4px;
    }
    .ma-empty { font-size: 12px; color: #999; padding: 6px 0; }
    .ma-add { display: inline-block; margin-top: 4px; font-size: 11px; }
    .ma-new-user {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 6px;
    }
    .ma-new-user[hidden] { display: none !important; }
    .ma-new-user input { flex: 1 1 110px; min-width: 110px; }
    .ma-new-user button { flex: 0 0 auto; }
`

function MassChooser({
  name,
  options,
  addTo,
}: {
  name: string
  options: Named[]
  addTo?: { url: string; label: string }
}) {
  if (options.length === 0) {
    return (
      <div className="ma-empty">
        Nothing defined yet.
        {addTo ? (
          <>
            {' '}
            <a href={addTo.url} target="_blank" rel="noopener">
              <i className="fas fa-plus"></i> {addTo.label}
            </a>
          </>
        ) : null}
      </div>
    )
  }
  return (
    <>
      <select name={name} className="form-control input-sm" defaultValue="">
        <option value="">Leave unchanged</option>
        {options.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
      {addTo ? (
        <a className="ma-add" href={addTo.url} target="_blank" rel="noopener">
          <i className="fas fa-plus"></i> {addTo.label}
        </a>
      ) : null}
    </>
  )
}

function MassAssign({
  selected,
  filters,
  onClear,
  onDone,
}: {
  selected: number[]
  filters: UnassignedFilters | undefined
  onClear(): void
  onDone(): void
}) {
  const [addUser, setAddUser] = useState(false)
  const [busy, setBusy] = useState(false)
  const shown = selected.length > 0
  const offices = filters?.offices ?? []
  const groups = filters?.groups ?? []
  const areas = filters?.areas ?? []
  const users = filters?.users ?? []

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (selected.length === 0 || busy) return
    const data = new FormData(e.currentTarget)
    const fields: Record<string, string> = {}
    data.forEach((value, key) => {
      if (key.startsWith('device_') && String(value) !== '') fields[key] = String(value)
    })
    /* Acelasi raspuns ca in index-1.js: nimic completat nu e o eroare, e un
       lucru de spus - altfel butonul pare stricat. */
    if (Object.keys(fields).length === 0) {
      toast('Nothing to change - fill in at least one field.', 'Nothing to do', 'info')
      return
    }
    if (!window.confirm(`Apply to ${selected.length} device(s)?`)) return
    setBusy(true)
    void massAssignDevices(selected, fields)
      .then((out) => {
        const changed = out.changed || selected.length
        toast(`${changed} device(s) assigned.`, 'Success')
        /* Masinile atribuite pleaca din lista - chiar asta inseamna: nu mai
           sunt "NEW". De-aia se cere lista din nou si se golesc bifele. */
        onDone()
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Please try again.', 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className={`ma-panel${shown ? '' : ''}`} id="massAssign" hidden={!shown}>
      <div className="ma-head">
        <div className="ma-count">
          <strong id="maCount">{selected.length}</strong> selected
        </div>
        <div className="ma-hint">Only the fields you fill in are written. The rest stay as they are.</div>
        <button type="button" className="btn btn-sm btn-default ma-clear" id="maClear" onClick={onClear}>
          Clear selection
        </button>
      </div>
      <form id="massAssignForm" className="ma-fields" method="post" action="/api/v2/inventoree/unassigned/mass-assign" onSubmit={onSubmit}>
        <div className="ma-field">
          <label>User</label>
          <select name="device_owner" className="form-control input-sm" id="maOwner" defaultValue="">
            <option value="">Leave unchanged</option>
            {users.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.name}
              </option>
            ))}
          </select>
          <a
            className="ma-add"
            href="#"
            id="maAddUser"
            onClick={(e) => {
              e.preventDefault()
              setAddUser((v) => !v)
            }}
          >
            <i className="fas fa-plus"></i> Add a user
          </a>
          <div className="ma-new-user" id="maNewUser" hidden={!addUser && users.length > 0}>
            <input type="text" className="form-control input-sm" id="maFirst" placeholder="First name" autoComplete="off" />
            <input type="text" className="form-control input-sm" id="maLast" placeholder="Last name" autoComplete="off" />
            <input type="email" className="form-control input-sm" id="maEmail" placeholder="Email" autoComplete="off" />
            <button
              type="button"
              className="btn btn-sm btn-default"
              id="maCreateUser"
              onClick={(e) => {
                const button = e.currentTarget
                const firstEl = document.getElementById('maFirst') as HTMLInputElement | null
                const lastEl = document.getElementById('maLast') as HTMLInputElement | null
                const mailEl = document.getElementById('maEmail') as HTMLInputElement | null
                const first = firstEl?.value.trim() ?? ''
                const last = lastEl?.value.trim() ?? ''
                const email = mailEl?.value.trim() ?? ''
                if (!first || !last || !email || selected.length === 0) return

                /* Capatul cere un id de masina numai pentru gardul de firma; omul
                   nou nu se leaga de ea. Panoul se vede doar cu masini bifate,
                   deci `selected[0]` exista intotdeauna. */
                button.disabled = true
                void quickAddOwner(selected[0], first, last, email)
                  .then((added) => {
                    /* Ales pe loc, nu doar adaugat in lista: omul l-a scris tocmai
                       fiindca despre el e vorba. Salvarea ramane butonul de mai
                       jos - acelasi lucru pe care il facea `device_owner.js`. */
                    const select = document.getElementById('maOwner') as HTMLSelectElement | null
                    if (select) {
                      select.appendChild(new Option(added.text, String(added.id), true, true))
                      select.value = String(added.id)
                    }
                    if (firstEl) firstEl.value = ''
                    if (lastEl) lastEl.value = ''
                    if (mailEl) mailEl.value = ''
                  })
                  .catch(() => window.alert('The person could not be added.'))
                  .finally(() => {
                    button.disabled = false
                  })
              }}
            >
              Add
            </button>
          </div>
        </div>
        <div className="ma-field">
          <label>Office</label>
          <MassChooser name="device_office" options={offices} addTo={{ url: '/settings/companies', label: 'Add an office' }} />
        </div>
        <div className="ma-field">
          <label>Group</label>
          <MassChooser name="device_group" options={groups} addTo={{ url: '/settings/customers/groups', label: 'Add a group' }} />
        </div>
        <div className="ma-field">
          <label>Area</label>
          <MassChooser name="device_area" options={areas} />
        </div>
        <div className="ma-field">
          <label>Auto-Update</label>
          <select name="device_auto_update" className="form-control input-sm" defaultValue="">
            <option value="">Leave unchanged</option>
            <option value="1">On</option>
            <option value="0">Off</option>
          </select>
        </div>
        <div className="ma-field ma-field-action">
          <button type="submit" className="btn btn-sm btn-theme" id="maApply" disabled={busy}>
            <i className="fas fa-user-check"></i> {busy ? 'Assigning...' : 'Assign selected'}
          </button>
        </div>
      </form>
    </div>
  )
}

export function UnassignedPage() {
  const [state, setState] = useTableState('unnasignedDevices', { start: 0, length: 25, order: { column: 5, dir: 'desc' }, search: '' })
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const filters = useQuery(unassignedFiltersQuery)
  const table = useQuery({
    ...unassignedTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const rows = table.data?.data ?? []
  const columns = unassignedColumns()

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
      return
    }
    if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => Number(r.ID))) : new Set())
      return
    }
    /*
     * Remove: butonul e scris de capat in HTML-ul randului (campul `actions`),
     * deci n-are cum sa aiba un onClick al lui - se prinde aici, ca
     * `Actions.removeDevice` in overview.js. `closest` fiindca apasarea
     * nimereste de cele mai multe ori iconita dinauntru, nu butonul.
     */
    /* "Suggested: X" din logarile AD - [Assign] e o asignare de mana, [Dismiss]
       nu-l mai propune pe aparatul asta. Aceeasi delegare ca Remove. */
    const suggestion = suggestionAction(target)
    if (suggestion) {
      suggestion.button.setAttribute('disabled', 'disabled')
      const call = suggestion.action === 'accept' ? acceptOwnerSuggestion : dismissOwnerSuggestion
      void call(suggestion.deviceId)
        .then((answer) => {
          toast(answer.message, suggestion.action === 'accept' ? 'Owner' : 'Suggestion', answer.status === 'OK' ? 'success' : 'error')
          void table.refetch()
        })
        .catch((err: unknown) => {
          suggestion.button.removeAttribute('disabled')
          toast(err instanceof Error ? err.message : 'Please try again.', 'Error', 'error')
        })
      return
    }

    const remove = target.closest('.remove-device')
    if (remove instanceof HTMLElement) {
      const deviceId = Number(remove.dataset.device ?? 0)
      if (deviceId < 1) return
      remove.setAttribute('disabled', 'disabled')
      void removeUnassignedDevice(deviceId)
        .then(() => {
          toast('Device successfully removed.', 'Success')
          setSelected((s) => {
            const next = new Set(s)
            next.delete(deviceId)
            return next
          })
          void table.refetch()
        })
        .catch((err: unknown) => {
          remove.removeAttribute('disabled')
          toast(err instanceof Error ? err.message : 'Please try again.', 'Error', 'error')
        })
    }
  }

  return (
    <Content>
      <style>{MASS_CSS}</style>
      <MassAssign
        selected={[...selected]}
        filters={filters.data}
        onClear={() => setSelected(new Set())}
        onDone={() => {
          setSelected(new Set())
          void table.refetch()
        }}
      />
      <div onClick={onTableClick}>
        <DataTable
          id="unnasignedDevices"
          columnTools
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(Number(r.ID)) })) as Record<string, unknown>[]}
          total={table.data?.recordsTotal ?? 0}
          fetching={table.isFetching}
          error={table.isError ? (table.error as Error).message : null}
          onRetry={() => void table.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId ?? r.ID)}
          details={(row) => (
            /* Fisa aparatului NOU - cele trei coloane din actionTabs.php, nu fisa
               generica: Details / Settings & debug / Assign. Dupa Save cu stapan
               masina PLEACA de aici, deci lista se cere din nou. */
            <UnassignedCard id={Number(row.ID ?? row.deviceId)} onAssigned={() => void table.refetch()} />
          )}
        />
      </div>
    </Content>
  )
}
